import { Buffer } from "buffer";
import { ReportLog } from "./ReportLog";
import Report from "./Report";
import Utils from "./Utils";
import SaapApi from "./SaapApi";
import SdkInfo from "./SdkInfo";

class BleHandler {
  static instance = null;
  static mListener = null;
  static mFrameCounter;
  static mFrameKey = null;
  mHandshakeCode = null;

  static getInstance(reuse) {
    if (reuse) {
      if (BleHandler.instance === null) {
        BleHandler.instance = new BleHandler();
      }
      return BleHandler.instance;
    } else {
      if (BleHandler.instance !== null && BleHandler.instance.isConnected) {
        BleHandler.instance.disconnect();
      }
      BleHandler.instance = new BleHandler();
      return BleHandler.instance;
    }
  }

  constructor() {
    this.device = null;
    this.isConnected = false;
    this.serviceUUID = "1b8420d8-7148-482d-a88b-b4715976f77b";
    this.characteristicUUIDs = {
      C1: "a9efecf1-5493-4101-a144-3ba0f6b3175a", // Send Token
      C3: "1e96e241-8684-41e2-bb58-5c1b25597afa", // Secure exchange of command
      C4: "b126896a-b0af-4f26-b880-839a853e803e", // Data received from device
    };
  }

  onDisconnected = (event) => {
    this.isConnected = false;
    console.log(`OnDisconnect!! The device ${event.target} is disconnected`);
  };

  handleCharacteristicValueChanged = (event) => {
    const value = event.target.value; // DataView of the received data
    const receivedData = new Uint8Array(value.buffer);
    this.processReport(receivedData);

    // // Process the data (e.g., convert to string or parse bytes)
    // const textDecoder = new TextDecoder("utf-8");
    // console.log("Decoded data:", textDecoder.decode(receivedData));
  };

  /**
   * Process reports
   * @param {Uint8Array} data - The received data
   */
  processReport = (data) => {
    console.log("Process Report:", Utils.toHexString(data));

    const rep = new Report(this.mContext, this.mToken, this.mAction, data);

    // Send all notifications to application except report
    if (rep.notification !== SaapApi.NOTIF_NONE) {
      this.mListener.onNotification(rep.notification, "");

      if (rep.notification === SaapApi.NOTIF_LOCK_UNLOCKED && this.mToken) {
        const frameParams = {};
        frameParams.id = this.mToken.assetId;
        frameParams.counter = this.mFrameCounter;
        frameParams.key = this.mFrameKey;
        this.mSdkInfo.saveFrameParams(frameParams);
      }
    }

    const j = rep.log.json();
    this.mListener.onNotification(SaapApi.NOTIF_DEBUG, j);
    this.mListener.onNotification(
      SaapApi.NOTIF_SRV_REPORT,
      Utils.createJwt(j),
      j
    );

    // Track completion
    if (
      rep.responsePending === 0 &&
      rep.responseType === SaapApi.RESPONSE_COMMAND
    ) {
      this.mListener.onNotification(SaapApi.NOTIF_TRANS_COMPLETED, "");
    }
  };

  connectToDevice = async () => {
    try {
      this.device = await navigator.bluetooth.requestDevice({
        filters: [
          // { name: "HBOX" }, // Filter by an exact name
          { manufacturerData: [{ companyIdentifier: 0x524c }] }, // Filter by manufacturer data
        ],
        // acceptAllDevices: true,
        // name: deviceId,
        optionalServices: [this.serviceUUID],
        // acceptAllAdvertisements: true, // to check adv data
      });

      console.log("Device:", this.device);

      this.device.addEventListener("advertisementreceived", (event) => {
        const mfg = event.manufacturerData.get(0x4c52);
        if (mfg) {
          console.log("Manufacturer Data:", new Uint8Array(mfg.buffer));
        }
      });

      // Add an event listener to detect when a device disconnects
      this.device.addEventListener(
        "gattserverdisconnected",
        this.onDisconnected
      );

      // Try to connect to the remote GATT Server running on the Bluetooth device
      const server = await this.device.gatt.connect();
      console.log("Device Connected:", server, this.device.gatt.connected);
      this.isConnected = this.device.gatt.connected;

      // Get the service from the Bluetooth device
      const service = await server.getPrimaryService(this.serviceUUID);
      console.log("Service obtained:", service);

      this.characteristic1 = await service.getCharacteristic(
        this.characteristicUUIDs.C1
      );
      this.characteristic3 = await service.getCharacteristic(
        this.characteristicUUIDs.C3
      );
      this.characteristic4 = await service.getCharacteristic(
        this.characteristicUUIDs.C4
      );
      console.log("Characteristics obtained");

      // let characteristic = await service.getCharacteristic(
      //   "0000abcd-0000-1000-8000-00805f9b34fb"
      // );
      // if (characteristic) {
      //   let val = await characteristic.readValue();
      //   console.log("CHAR VALUE:", val);
      // } else {
      //   console.log("No CHAR to read BT ADDR");
      // }

      // Subscribe to response data (through C4)
      await this.characteristic4.startNotifications();
      // Setup listener for C4
      this.characteristic4.addEventListener(
        "characteristicvaluechanged",
        this.handleCharacteristicValueChanged
      );
    } catch (error) {
      console.log(`There was an error in BT connection: ${error}`);
    }
  };

  async disconnect() {
    if (this.device) {
      this.device.gatt.disconnect();
    } else {
      this.isConnected = false;
    }
  }

  monitorCharacteristic = (characteristicUUID, callback) => {
    if (!this.isConnected || !this.device)
      throw new Error("Device not connected");
    this.device.monitorCharacteristicForService(
      this.serviceUUID,
      characteristicUUID,
      (error, characteristic) => {
        if (error) {
          console.error("Monitor error:", error);
          return;
        }
        const data = Buffer.from(characteristic.value, "base64").toString(
          "hex"
        );
        callback(data);
      }
    );
  };

  requestMtu = async (mtuSize) => {
    if (!this.isConnected || !this.device)
      throw new Error("Device not connected");
    const mtu = await this.device.requestMTU(mtuSize);
    return mtu;
  };

  readRssi = async () => {
    if (!this.isConnected || !this.device)
      throw new Error("Device not connected");
    const rssi = await this.device.readRSSI();
    return rssi;
  };

  handshakeCode = () => {
    if (this.mHandshakeCode == null) {
      this.mHandshakeCode = [
        Math.floor(Math.random() * 256),
        Math.floor(Math.random() * 256),
      ];
      console.log("Hadshake code", this.mHandshakeCode);
    }
    return this.mHandshakeCode;
  };

  syncTime = () => {
    let utc = Math.floor(new Date().getTime() / 1000);
    const byteArray = new Uint8Array(4); // Assuming 4-byte integer

    for (let i = 0; i < 4; i++) {
      byteArray[i] = utc & 0xff;
      utc >>= 8;
    }

    return byteArray;
  };

  frameMessage = async (token, action, sdkInfo) => {
    const handshake = this.handshakeCode();
    const utc = this.syncTime();
    const idx = Math.floor(Math.random() * 10);
    var sdkcode = Buffer.from(sdkInfo.sdkId, "base64");

    console.log("SDK Code:", sdkInfo.sdkId);
    console.log("SDK Code Bytes:", sdkcode);

    const data = new Uint8Array([
      action, // 1 byte action
      handshake[0], // 2 bytes handshake
      handshake[1],
      utc[0], // 4 bytes sync time
      utc[1],
      utc[2],
      utc[3],
      sdkcode[idx], // 7 bytes of sdkcode starting from random index
      sdkcode[idx + 1],
      sdkcode[idx + 2],
      sdkcode[idx + 3],
      sdkcode[idx + 4],
      sdkcode[idx + 5],
      sdkcode[idx + 6],
      0, // Frame counter
      idx, // random index between 0-10
    ]);

    if (this.mFrameKey == null) {
      this.mFrameKey = await Utils.createKey(
        sdkcode,
        token.transId,
        token.tokenData
      );
    }
    let encData = Utils.encryptMsg(data, this.mFrameKey);

    console.log("Hashed Frame Key:", Utils.toHexString(this.mFrameKey));
    console.log("Frame Data:", Utils.toHexString(data));
    console.log("Encoded Frame Data:", Utils.toHexString(encData));
    return encData;
  };

  frameMessage1 = async (action, pinCode) => {
    const transId = 0x475466ff;
    const tokenData = "e04b4e542405e0c1e1d2c9e17fd21b10b434d44f74fd";
    const pinCodeBytes = Uint8Array.from(
      pinCode.split("").map((c) => parseInt(c, 10))
    );
    console.log("Pin Code Bytes:", pinCodeBytes);

    const boxId = Uint8Array.from([0]); // randIdx & 0xff
    const sdkcode = new Uint8Array([...boxId, ...pinCodeBytes]);
    const actionBytes = Uint8Array.from([action & 0xff]);
    const handshake = this.handshakeCode();
    const utc = this.syncTime();
    const counter = Uint8Array.from([0xfe]);
    const idx = Uint8Array.from([1]);

    const data = new Uint8Array([
      ...actionBytes, // 1
      ...handshake, // 2
      ...utc, // 4
      ...sdkcode, // 7
      ...counter, // 1
      ...idx, // 1
    ]);

    if (!this.mFrameKey) {
      // 4c52, 6 bytes padding
      const extraBytes = [
        0x04, 0x0c, 0x05, 0x02, 0x55, 0x55, 0x55, 0x55, 0x55, 0x55,
      ];
      const sdkcodeAlt = new Uint8Array([...pinCodeBytes, ...extraBytes]);
      const sdkcodeX = Buffer.from(sdkcodeAlt);
      this.mFrameKey = await Utils.createKey(sdkcodeX, transId, tokenData);
    }

    let encData = Utils.encryptMsg(data, this.mFrameKey);

    console.log("FrameData   :", data);
    console.log("FrameDataHex:", Utils.toHexString(data));
    console.log("sdkcode     :", Utils.toHexString(sdkcode));
    console.log("Frame Key :", this.mFrameKey);
    console.log("Encoded FrameData:", Utils.toHexString(encData));
    return encData;
  };

  async writeData(data, len) {
    let i = 0;
    while (i < len) {
      let part;
      let l;

      l = len - i > 64 ? 64 : len - i;

      part = data.slice(i, l);
      i += l;

      await this.characteristic1.writeValue(part);
    }
  }

  processPinUnlock = async (ctx, action, sdkInfo, listener, pin) => {
    console.log("PIN Unlock: ", pin);

    this.mContext = ctx;
    this.mToken = null;
    this.mAction = action;
    this.mSdkInfo = sdkInfo;
    this.mListener = listener;

    this.reportLog = new ReportLog(null);
    await this.connectToDevice();

    let data3 = await this.frameMessage1(action, pin);
    await this.characteristic3.writeValue(data3);
  };

  processTokenUnlock = async (ctx, token, action, sdkInfo, listener) => {
    this.mContext = ctx;
    this.mToken = token;
    this.mAction = action;
    this.mSdkInfo = sdkInfo;
    this.mListener = listener;

    this.reportLog = new ReportLog(this.mToken);

    await this.connectToDevice(token.btAddr);

    // Send token
    const encoder = new TextEncoder();
    const encodedString = encoder.encode(token.tokenData);
    await this.characteristic1.writeValue(encodedString);

    // send command
    let data3 = await this.frameMessage(token, action, sdkInfo);
    await this.characteristic3.writeValue(data3);
  };

  processCommand = async (ctx, action, sdkInfo, listener, pin) => {
    console.log("SDK Info", sdkInfo);
    console.log("SDK Action", action);
    console.log("PIN ", pin);
    switch (action) {
      case SaapApi.LOCKER_ACTION_GET_PIN:
        break;
      case SaapApi.LOCKER_ACTION_SET_MASTER_PIN:
        break;
      case SaapApi.LOCKER_ACTION_UNLOCK_WITH_PIN:
        await this.processPinUnlock(ctx, action, sdkInfo, listener, pin);
        break;
    }
  };

  processToken = async (ctx, token, action, sdkInfo, listener) => {
    console.log("BLE Process Token", token.tokenData);
    console.log("SDK Info", sdkInfo);
    console.log("SDK Action", action);
    switch (action) {
      case SaapApi.LOCKER_ACTION_GET_PIN:
        break;
      case SaapApi.LOCKER_ACTION_SET_MASTER_PIN:
        break;
      case SaapApi.LOCKER_ACTION_UNLOCK:
      case SaapApi.LOCKER_ACTION_SERVICE_UNLOCK:
        await this.processTokenUnlock(ctx, token, action, sdkInfo, listener);
        break;
    }
  };
}

export default BleHandler;
